import { z } from 'zod';
import { ehDataISO } from './date.js';
import { FORMA_PAGAMENTO, INTENCAO, TIPO_TRANSACAO } from './types.js';

const valores = <T extends Record<string, string>>(obj: T) =>
  Object.values(obj) as [T[keyof T], ...T[keyof T][]];

export const dataISOSchema = z.string().refine(ehDataISO, {
  message: 'Data deve estar em YYYY-MM-DD e existir no calendário',
});

/**
 * Um lançamento extraído de uma mensagem.
 *
 * `valor_centavos` é inteiro e não-negativo: a direção do dinheiro vem de
 * `tipo`, nunca do sinal. Um valor negativo aqui seria ambíguo com uma saída.
 */
export const lancamentoExtraidoSchema = z.object({
  tipo: z.enum(valores(TIPO_TRANSACAO)),
  valor_centavos: z.number().int().nonnegative(),
  descricao: z.string().trim().min(1).max(280),
  categoria_sugerida: z.string().trim().max(80).nullable(),
  contraparte: z.string().trim().max(120).nullable(),
  forma_pagamento: z.enum(valores(FORMA_PAGAMENTO)).nullable(),
  data_ocorrencia: dataISOSchema,
  confianca: z.number().min(0).max(1),
  observacao: z.string().trim().max(280).nullable(),
});
export type LancamentoExtraido = z.infer<typeof lancamentoExtraidoSchema>;

/** Período que o usuário pediu numa consulta ("mês passado", "julho", "últimos 30 dias"). */
export const periodoConsultaSchema = z.object({
  inicio: dataISOSchema,
  fim: dataISOSchema,
  rotulo: z.string().trim().max(60),
});
export type PeriodoConsulta = z.infer<typeof periodoConsultaSchema>;

export const consultaExtraidaSchema = z.object({
  periodo: periodoConsultaSchema,
  tipo: z.enum(valores(TIPO_TRANSACAO)).nullable(),
  categoria: z.string().trim().max(80).nullable(),
  quer_planilha: z.boolean().default(false),
  agrupar_por_categoria: z.boolean().default(false),
});
export type ConsultaExtraida = z.infer<typeof consultaExtraidaSchema>;

/**
 * Contrato de saída do LLM de extração.
 *
 * Diverge da especificação em um ponto, deliberadamente: a spec desenha um
 * objeto plano com um lançamento só, mas também exige que "gastei 50 no uber e
 * 120 no almoço" gere duas transações. Um array resolve os dois casos — uma
 * mensagem com um único lançamento simplesmente traz um array de tamanho 1.
 */
export const respostaExtracaoSchema = z.object({
  intencao: z.enum(valores(INTENCAO)),
  lancamentos: z.array(lancamentoExtraidoSchema).max(10).default([]),
  consulta: consultaExtraidaSchema.nullable().default(null),
  observacao: z.string().trim().max(280).nullable().default(null),
});
export type RespostaExtracao = z.infer<typeof respostaExtracaoSchema>;

/**
 * Valida a saída crua do LLM, tolerando o markdown que modelos às vezes
 * teimam em colocar em volta do JSON.
 */
export function parseRespostaExtracao(bruto: string): RespostaExtracao {
  const semCerca = bruto
    .trim()
    .replace(/^```(?:json)?\s*/i, '')
    .replace(/\s*```$/, '');
  return respostaExtracaoSchema.parse(JSON.parse(semCerca));
}
